import {
  Routes,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import {
  ConsoleTransport,
  ErrorsInstrumentation,
  FetchTransport,
  ReactIntegration,
  ReactRouterVersion,
  initializeFaro as coreInit,
} from '@grafana/faro-react';
import { propagation } from '@opentelemetry/api';
import { B3Propagator } from '@opentelemetry/propagator-b3';

// import type { Faro as FaroType } from '@grafana/faro-core';
import {
  SessionInstrumentation,
  WebVitalsInstrumentation,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

let faroInstance = null;

// Helper function to escape regex special characters in a string
// Used to safely construct a RegExp from a URL string
function escapeRegExp(string) {
  return String(string).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Helper to check required env vars at runtime
// Throws an error if a required environment variable is missing
function getRequiredEnvVar(key) {
  const value = process.env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

// Base URL for the Application Specific Journey (ASJ) backend
const baseUrl = getRequiredEnvVar('REACT_APP_BASE_URL_TT');
// Regex to match URLs starting with the baseUrl for trace propagation
const collectRegex = new RegExp(`^${escapeRegExp(baseUrl)}`);

// Tracing instrumentation config: propagate trace headers to ASJ backend
const instrumentationOptions = {
  propagateTraceHeaderCorsUrls: [collectRegex],
};

// Set B3 as the global trace context propagator (Zipkin-compatible)
propagation.setGlobalPropagator(new B3Propagator());

/**
 * Initializes the Grafana Faro agent for client-side observability.
 * This function sets up various instrumentations (errors, tracing, React integration)
 * and transports (Fetch for sending data, Console for development).
 * It ensures that Faro is initialized only once (singleton pattern).
 *
 * @returns The initialized Faro instance.
 */
export function initializeFaro() {
  // Ensure Faro is initialized only once (singleton)
  if (faroInstance) {
    return faroInstance;
  }

  // Determine if running in production mode
  const isProd = process.env.MODE === 'development'; // This seems inverted. 'development' means NOT production.
                                                     // Should likely be: const isProd = process.env.NODE_ENV === 'production';
                                                     // Or: const isDev = process.env.NODE_ENV === 'development';
                                                     // For now, I'll keep your original logic for isProd based on 'MODE' env variable.

  const collectorUrl = `${getRequiredEnvVar('REACT_APP_BASE_URL_TT')}/collect`;
  const apiKey = getRequiredEnvVar(
    'REACT_APP_OTEL_COLLECTOR_SUBSCRIPTION_HEADER_KEY'
  );

  const appVersion =
    process.env.REACT_APP_OTEL_COLLECTOR_APP_VERSION || '1.0.0';
  const appMode =
    process.env.REACT_APP_OTEL_COLLECTOR_APP_MODE ||
    (isProd ? 'production' : 'development');

  faroInstance = coreInit({
    instrumentations: [
      new ErrorsInstrumentation(),
      new TracingInstrumentation({ instrumentationOptions }),
      new SessionInstrumentation(),
      new ReactIntegration({
        router: {
          version: ReactRouterVersion.V6,
          dependencies: {
            createRoutesFromChildren,
            matchRoutes,
            Routes,
            useLocation,
            useNavigationType,
          },
        },
      }),
      // Only include WebVitalsInstrumentation in production
      ...(isProd ? [new WebVitalsInstrumentation()] : []),
    ],
    transports: [
      // Send telemetry to backend collector
      new FetchTransport({
        url: collectorUrl,
        apiKey,
      }),
      // Log telemetry to console in development only
      ...(!isProd ? [new ConsoleTransport()] : []),
    ],
    app: {
      name: 'translation-tool-dev-web',
      version: appVersion,
      environment: appMode,
    },
    batching: {
      itemLimit: 100, // Batch up to 100 items per request
    },
    // Enable web vitals and resource tracking in production
    trackWebVitalsAttribution: isProd,
    trackResources: isProd,
    webVitalsInstrumentation: {
      reportAllChanges: isProd,
      trackAttribution: isProd,
    },
  });

  if (!isProd) {
    // Only log in development
    console.log('✅ Faro was initialized');
  }

  // --- IMPORTANT CHANGE HERE ---
  // Now that faroInstance is initialized, call setUserContext
  // Get user data from localStorage after Faro is initialized
  const user = JSON.parse(localStorage.getItem("app-auth"));
  setUserContext(user);
  console.log("oteluser------------", user?.employeeName, user?.email); // Added optional chaining for safety
  console.log("api key", apiKey);
  return faroInstance;
}

export function setUserContext(user) {
  if (!user || !faroInstance) {
    console.warn("Faro instance or user data not available, cannot set user context.");
    return;
  }

  faroInstance.api.setUser({
    id: user.id,
    username: user.employeeName,
    email: user.email,
  });

  faroInstance.api.pushEvent("user_logged_in", {
    userId: user.id,
    userName: user.employeeName,
    userEmail: user.email,
    loginTime: new Date().toISOString(),
  });
}